'use client';

import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  AgentLogsMockup,
  SignalsMockup,
  ABTestsMockup,
  SlackAlertsMockup,
  CodebaseResolutionMockup,
} from './sentrial-mockups';

interface SubFeature {
  id: string;
  label: string;
  heading: string;
  description: string;
  link: string;
  mockup: React.ReactNode;
}

interface Section {
  id: string;
  number: string;
  bgImage: string;
  subFeatures: SubFeature[] | [SubFeature, SubFeature];
}

const sections: Section[] = [
  {
    id: 'section-1',
    number: '1',
    bgImage: '/backgrounds/bg-2.webp',
    subFeatures: [
      {
        id: 'agent-logs',
        label: 'OBSERVE',
        heading: 'Inspect your agent runs',
        description:
          'Inspect captured messages, tool calls and responses alongside timing and estimated token costs.',
        link: 'Start tracing sessions',
        mockup: <AgentLogsMockup />,
      },
    ],
  },
  {
    id: 'section-2',
    number: '2',
    bgImage: '/backgrounds/bg-3.webp',
    subFeatures: [
      {
        id: 'signals',
        label: 'EVALUATE',
        heading: 'Review potential failures',
        description:
          'Define behaviors to watch, inspect the evidence behind matches, and review missed or uncertain results.',
        link: 'Set up evaluations',
        mockup: <SignalsMockup />,
      },
      {
        id: 'ab-tests',
        label: 'EVALUATE',
        heading: 'Compare changes on the same cases',
        description:
          'Compare saved runs and evaluate a change on the same cases before deciding what to ship.',
        link: 'Run an experiment',
        mockup: <ABTestsMockup />,
      },
    ],
  },
  {
    id: 'section-3',
    number: '3',
    bgImage: '/backgrounds/bg-1.webp',
    subFeatures: [
      {
        id: 'slack-alerts',
        label: 'ACT',
        heading: 'Configure alerts for your team',
        description:
          'Choose where alerts are delivered and investigate the runs behind each issue.',
        link: 'Configure alerts',
        mockup: <SlackAlertsMockup />,
      },
      {
        id: 'codebase-resolution',
        label: 'ACT',
        heading: 'Investigate failures in context',
        description:
          'Use traces and tool results to investigate a failure, then review a proposed change before applying it.',
        link: 'See how resolution works',
        mockup: <CodebaseResolutionMockup />,
      },
    ],
  },
];

function FeatureSlide({ section }: { section: Section }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasTwoFeatures = section.subFeatures.length === 2;

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  const sub1Opacity = useTransform(scrollYProgress, [0.2, 0.45], [1, hasTwoFeatures ? 0 : 1]);
  const sub1Y = useTransform(scrollYProgress, [0.2, 0.45], [0, hasTwoFeatures ? -40 : 0]);
  const sub1PointerEvents = useTransform(scrollYProgress, (v) =>
    hasTwoFeatures && v > 0.35 ? 'none' : 'auto'
  );

  const sub2Opacity = useTransform(scrollYProgress, [0.55, 0.8], [0, 1]);
  const sub2Y = useTransform(scrollYProgress, [0.55, 0.8], [40, 0]);
  const sub2PointerEvents = useTransform(scrollYProgress, (v) => (v <= 0.65 ? 'none' : 'auto'));

  return (
    <>
      {/* Desktop View - Sticky Scroll */}
      <div
        ref={containerRef}
        className={`hidden lg:block w-full relative ${hasTwoFeatures ? 'h-[250vh]' : 'h-[130vh]'}`}
      >
        {/* Scroll-snap markers — tiny 1px targets (mandatory snap is unreliable on
            elements taller than the viewport, so we don't snap the container itself).
            One at the sub-feature-1 rest state, and for two-feature sections a second
            at the sub-feature-2 rest state (~progress 0.85) so scrolling drifts to it
            instead of stalling in the cross-fade gap. */}
        <div
          aria-hidden
          className="snap-start pointer-events-none absolute left-0 top-0 h-px w-full"
        />
        {hasTwoFeatures && (
          <div
            aria-hidden
            className="snap-start pointer-events-none absolute left-0 top-[128vh] h-px w-full"
          />
        )}
        <div className="h-screen sticky top-0 w-full overflow-hidden bg-card flex items-center justify-center">
          <svg
            className="absolute inset-0 w-full h-full z-0 opacity-20 pointer-events-none"
            fill="none"
            preserveAspectRatio="none"
          >
            <line x1="0" y1="0" x2="100%" y2="100%" stroke="#94A3B8" strokeWidth="0.5" />
            <line x1="100%" y1="0" x2="0" y2="100%" stroke="#94A3B8" strokeWidth="0.5" />
            <line x1="0" y1="25%" x2="100%" y2="25%" stroke="#94A3B8" strokeWidth="0.5" />
            <line x1="0" y1="75%" x2="100%" y2="75%" stroke="#94A3B8" strokeWidth="0.5" />
            <line x1="25%" y1="0" x2="25%" y2="100%" stroke="#94A3B8" strokeWidth="0.5" />
            <line x1="75%" y1="0" x2="75%" y2="100%" stroke="#94A3B8" strokeWidth="0.5" />
          </svg>

          <motion.div
            initial={{ opacity: 0, scale: 0.8, x: -50 }}
            whileInView={{ opacity: 1, scale: 1, x: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="absolute top-24 left-12 lg:left-24 z-20 origin-top-left pointer-events-none"
          >
            <span
              className="text-[120px] sm:text-[160px] lg:text-[240px] font-light text-gold leading-[0.8]"
              style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 200 }}
            >
              {section.number}
            </span>
          </motion.div>

          <div className="relative w-[95%] max-w-[1400px] h-[80vh] min-h-[600px] flex shadow-2xl z-10">
            <div
              className="absolute inset-0 z-0 dark:invert dark:hue-rotate-180"
              style={{
                backgroundImage: `url(${section.bgImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />

            {/* SubFeature 1 */}
            <motion.div
              style={{
                opacity: sub1Opacity,
                y: sub1Y,
                pointerEvents: sub1PointerEvents,
              }}
              className="absolute inset-0 z-10 w-full h-full flex flex-col lg:flex-row"
            >
              <div className="w-full lg:w-[55%] h-full flex items-center justify-center p-8 lg:p-16">
                <figure className="w-full max-w-[700px]">
                  {section.subFeatures[0].mockup}
                  <figcaption className="mt-3 mx-auto w-fit max-w-full border border-border-subtle bg-card px-3 py-1.5 text-center text-label text-text-secondary">
                    Illustration · sample data
                  </figcaption>
                </figure>
              </div>
              <div className="w-full lg:w-[45%] h-full flex items-center justify-center p-8 lg:p-16">
                <div className="w-full h-auto bg-card/70 backdrop-blur-xl p-10 lg:p-14 flex flex-col justify-center border border-white/50 dark:border-white/10 shadow-card-lg">
                  <span className="text-label font-bold tracking-wider text-gold uppercase mb-6 block">
                    {section.subFeatures[0].label}
                  </span>
                  <h3
                    className="text-[32px] sm:text-[40px] lg:text-[48px] font-light text-ink leading-[1.1] mb-6"
                    style={{ fontFamily: 'Nunito, sans-serif' }}
                  >
                    {section.subFeatures[0].heading}
                  </h3>
                  <p
                    className="text-[16px] lg:text-[18px] text-text-secondary leading-[1.6] mb-10"
                    style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 300 }}
                  >
                    {section.subFeatures[0].description}
                  </p>
                  <a
                    href="/login"
                    className="inline-flex items-center gap-2 text-[15px] text-ink font-medium hover:text-gold transition-colors group mt-auto"
                  >
                    {section.subFeatures[0].link}
                    <span className="group-hover:translate-x-1 transition-transform duration-200">
                      →
                    </span>
                  </a>
                </div>
              </div>
            </motion.div>

            {/* SubFeature 2 (only if present) */}
            {hasTwoFeatures && (
              <motion.div
                style={{
                  opacity: sub2Opacity,
                  y: sub2Y,
                  pointerEvents: sub2PointerEvents,
                }}
                className="absolute inset-0 z-10 w-full h-full flex flex-col lg:flex-row"
              >
                <div className="w-full lg:w-[55%] h-full flex items-center justify-center p-8 lg:p-16">
                  <figure className="w-full max-w-[700px]">
                    {section.subFeatures[1].mockup}
                    <figcaption className="mt-3 mx-auto w-fit max-w-full border border-border-subtle bg-card px-3 py-1.5 text-center text-label text-text-secondary">
                      Illustration · sample data
                    </figcaption>
                  </figure>
                </div>
                <div className="w-full lg:w-[45%] h-full flex items-center justify-center p-8 lg:p-16">
                  <div className="w-full h-auto bg-card/70 backdrop-blur-xl p-10 lg:p-14 flex flex-col justify-center border border-white/50 dark:border-white/10 shadow-card-lg">
                    <span className="text-label font-bold tracking-wider text-gold uppercase mb-6 block">
                      {section.subFeatures[1].label}
                    </span>
                    <h3
                      className="text-[32px] sm:text-[40px] lg:text-[48px] font-light text-ink leading-[1.1] mb-6"
                      style={{ fontFamily: 'Nunito, sans-serif' }}
                    >
                      {section.subFeatures[1].heading}
                    </h3>
                    <p
                      className="text-[16px] lg:text-[18px] text-text-secondary leading-[1.6] mb-10"
                      style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 300 }}
                    >
                      {section.subFeatures[1].description}
                    </p>
                    <a
                      href="/login"
                      className="inline-flex items-center gap-2 text-[15px] text-ink font-medium hover:text-gold transition-colors group mt-auto"
                    >
                      {section.subFeatures[1].link}
                      <span className="group-hover:translate-x-1 transition-transform duration-200">
                        →
                      </span>
                    </a>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile View - Stacked */}
      <div className="block lg:hidden w-full px-4 py-12 space-y-12">
        {section.subFeatures.map((subFeature) => (
          <div
            key={subFeature.id}
            className="w-full flex flex-col shadow-card-lg border border-border-subtle overflow-hidden"
          >
            <div className="w-full p-6 sm:p-8 flex items-center justify-center min-h-[300px] relative">
              <div
                className="absolute inset-0 z-0 dark:invert dark:hue-rotate-180"
                style={{
                  backgroundImage: `url(${section.bgImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <figure className="relative z-10 w-full">
                {subFeature.mockup}
                <figcaption className="mt-3 mx-auto w-fit max-w-full border border-border-subtle bg-card px-3 py-1.5 text-center text-label text-text-secondary">
                  Illustration · sample data
                </figcaption>
              </figure>
            </div>
            <div className="w-full bg-card p-8">
              <span className="text-tiny font-bold tracking-wider text-gold uppercase mb-4 block">
                {subFeature.label}
              </span>
              <h3
                className="text-[28px] sm:text-[32px] font-light text-ink leading-[1.2] mb-4"
                style={{ fontFamily: 'Nunito, sans-serif' }}
              >
                {subFeature.heading}
              </h3>
              <p
                className="text-[15px] text-text-secondary leading-[1.6] mb-6"
                style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 300 }}
              >
                {subFeature.description}
              </p>
              <a
                href="/login"
                className="inline-flex items-center gap-2 text-body text-ink font-medium hover:text-gold transition-colors"
              >
                {subFeature.link}
                <span>→</span>
              </a>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export function FeaturesSection() {
  return (
    <section id="features" className="relative">
      {sections.map((section) => (
        <FeatureSlide key={section.id} section={section} />
      ))}
    </section>
  );
}
